import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { environment } from './environments/environment';
import { ToastModule } from 'primeng/toast';
import { AuthService } from './app/services/auth.service';
import { forkJoin, Subscription } from 'rxjs';
import { ClientService } from './app/services/client.service';
import { CommonService } from './app/services/common.service';
import { IClientDetail, IClientLicense, ClientLicenses, IClientSubscriptionStatus } from './app/models/client.models';
import { IPageDefaultSetting } from './app/models/common';
import { VmsSessionStorageService } from './app/services/vms-session-storage.service';
import { sessionStorageKey } from './app/common/enum';
import { LoadingComponent } from './app/layout/component/loading.component';
import { PRIME_NG_CONFIG } from 'primeng/config';
import { ThemePreferenceService } from './app/services/theme-preference.service';
import { ClientBrandingService } from './app/services/client-branding.service';
import { ConnectivityService } from './app/services/connectivity.service';
import { LayoutService } from './app/layout/service/layout.service';
import { MobileNativeService } from './app/services/mobile-native.service';
import { OfflineSyncService } from './app/services/offline-sync.service';
import { PlatformService } from './app/services/platform.service';
import { IAuthenticate } from './app/models/authenticate';
import { DropdownDataItem } from './app/models/dropdown-data-soure';
import { ClientContextRefreshService } from './app/services/client-context-refresh.service';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [RouterModule, ToastModule, LoadingComponent],
    template: `
        <p-toast></p-toast>
        <app-loading></app-loading>
        <router-outlet></router-outlet>
    `
})
export class AppComponent implements OnInit, OnDestroy {
    authService = inject(AuthService);
    clientService = inject(ClientService);
    commonService = inject(CommonService);
    vmsSessionStorageService = inject(VmsSessionStorageService);
    primengConfig = inject(PRIME_NG_CONFIG);
    themePreferenceService = inject(ThemePreferenceService);
    clientBrandingService = inject(ClientBrandingService);
    platformService = inject(PlatformService);
    mobileNativeService = inject(MobileNativeService);
    connectivityService = inject(ConnectivityService);
    offlineSyncService = inject(OfflineSyncService);
    layoutService = inject(LayoutService);
    router = inject(Router);
    clientContextRefresh = inject(ClientContextRefreshService);

    private authSubscription?: Subscription;
    private bootstrapUserId: number | null = null;
    private midnightTimerId?: ReturnType<typeof setTimeout>;
    private readonly onVisibilityChange = () => {
        if (document.visibilityState === 'visible' && this.authService.isAuthenticated()) {
            this.clientContextRefresh.refreshAll();
        }
    };

    constructor() {
        console.log('env:', environment.name);

        if (this.platformService.isNativeMobile) {
            this.layoutService.applyMobileDefaults();
        }

        this.connectivityService.onReconnect(() => {
            void this.offlineSyncService.flush();
        });

        this.authSubscription = this.authService.currentUser.subscribe((user) => {
            console.log('user:', user);
            this.themePreferenceService.initializeFromAuthUser(user);

            if (!user) {
                this.bootstrapUserId = null;
                this.commonService.resetBootstrapState();
                return;
            }

            void this.mobileNativeService.initialize();

            const roleId = user.userRoleId != null ? Number(user.userRoleId) : null;
            if (roleId == null || !Number.isFinite(roleId)) {
                return;
            }

            this.commonService.userRoleId = roleId;

            const userChanged = this.bootstrapUserId != null && this.bootstrapUserId !== user.id;
            this.bootstrapUserId = user.id;

            if (userChanged) {
                this.commonService.resetBootstrapState();
            }

            this.updateCommonService();

            if (!this.commonService.isBootstrapReady()) {
                this.loadUserBootstrap(user);
            }
        });
    }

    ngOnInit() {
        try {
            this.primengConfig.overlayOptions = {
                appendTo: 'body'
            };
        } catch (error) {
            console.error('Error setting PRIME_NG_CONFIG:', error);
        }

        if (!this.authService.isAuthenticated()) {
            if (localStorage.getItem('UserDetails')) {
                console.log('Token is invalid or expired. Clearing stale session...');
                this.authService.logout();
            }
            return;
        }

        this.updateCommonService();

        if (this.commonService.hasBootstrapData()) {
            this.commonService.IsMenuCodesLoaded = true;
            this.commonService.menuCodesLoaded$.next(true);
        }

        this.commonService.applyEffectiveSubscriptionDates();
        this.scheduleUtcMidnightRefresh();

        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this.onVisibilityChange);
        }
    }

    ngOnDestroy(): void {
        this.authSubscription?.unsubscribe();
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.onVisibilityChange);
        }
        if (this.midnightTimerId != null) {
            clearTimeout(this.midnightTimerId);
        }
    }

    private loadUserBootstrap(_user: IAuthenticate) {
        const bootstrapRequests = {
            menuCodes: this.clientService.getMenuCodes(),
            clientLicenses: this.clientService.getClientLicenses(),
            clientSubscription: this.clientService.getClientSubscription(),
            ...(this.commonService.IsClientDetailLoaded
                ? {}
                : { clientDetail: this.commonService.loadClientDetail() }),
            ...(this.commonService.IsPageDefaultSettingLoaded
                ? {}
                : { pageDefaultSetting: this.commonService.loadPageDefaultSetting() })
        };

        forkJoin(bootstrapRequests).subscribe({
            next: (result) => {
                const menuCodes = this.normalizeMenuCodes(result.menuCodes);
                const clientLicenses = this.normalizeClientLicenses(result.clientLicenses);
                const clientSubscription = result.clientSubscription;

                this.vmsSessionStorageService.set(sessionStorageKey.MenuCodes, menuCodes);
                this.vmsSessionStorageService.set(sessionStorageKey.ClientLicenses, clientLicenses);
                this.vmsSessionStorageService.set(sessionStorageKey.ClientLicense, clientLicenses);
                this.vmsSessionStorageService.set(sessionStorageKey.ClientSubscription, clientSubscription);
                this.commonService.menuCodes = menuCodes;
                this.commonService.clientLicenses = new ClientLicenses(clientLicenses);
                this.commonService.setClientSubscriptionStatus(clientSubscription);
                this.commonService.IsMenuCodesLoaded = true;
                this.commonService.menuCodesLoaded$.next(true);

                const clientDetail = result.clientDetail as IClientDetail | undefined;
                if (clientDetail) {
                    this.commonService.IsClientDetailLoaded = true;
                    this.commonService.clientDetail = clientDetail;
                    this.vmsSessionStorageService.set(sessionStorageKey.ClientDetail, clientDetail);
                    this.clientBrandingService.applyBranding(clientDetail?.clientName);
                    this.clientBrandingService.setClientLogo(clientDetail?.clientLogo ?? null);
                }

                const pageDefaultSetting = result.pageDefaultSetting as IPageDefaultSetting | undefined;
                if (pageDefaultSetting) {
                    this.commonService.IsPageDefaultSettingLoaded = true;
                    this.commonService.pageDefaultSetting = pageDefaultSetting;
                    this.vmsSessionStorageService.set(sessionStorageKey.PageDefaultSetting, pageDefaultSetting);
                }

                if (this.commonService.isLocked()) {
                    this.router.navigate(['/subscription-locked']);
                }
            },
            error: (err) => {
                console.error('Failed to load bootstrap data.', err);
                this.commonService.IsMenuCodesLoaded = true;
                this.commonService.menuCodesLoaded$.next(true);
            }
        });
    }

    updateCommonService() {
        const clientLicenses = this.normalizeClientLicenses(this.vmsSessionStorageService.get(sessionStorageKey.ClientLicenses));
        this.commonService.clientLicenses = new ClientLicenses(clientLicenses);
        const storedSubscription = this.vmsSessionStorageService.get(sessionStorageKey.ClientSubscription) as IClientSubscriptionStatus | null;
        if (storedSubscription) {
            this.commonService.setClientSubscriptionStatus(storedSubscription);
        } else {
            this.commonService.clientSubscriptionStatus = undefined;
        }
        this.commonService.pageDefaultSetting = this.vmsSessionStorageService.get(sessionStorageKey.PageDefaultSetting);
        this.commonService.menuCodes = this.normalizeMenuCodes(this.vmsSessionStorageService.get(sessionStorageKey.MenuCodes));
        this.commonService.clientDetail = this.vmsSessionStorageService.get(sessionStorageKey.ClientDetail);
        if (this.commonService.clientDetail?.clientName) {
            this.clientBrandingService.applyBranding(this.commonService.clientDetail.clientName);
        }
        if (this.commonService.clientDetail) {
            this.clientBrandingService.setClientLogo(this.commonService.clientDetail.clientLogo ?? null);
        }
    }

    private normalizeMenuCodes(menuCodes: DropdownDataItem[] | null | undefined): DropdownDataItem[] {
        return (menuCodes ?? []).map((code) => ({
            ...code,
            value: code.value ?? (code as { Value?: string }).Value
        }));
    }

    private normalizeClientLicenses(licenses: IClientLicense[] | null | undefined): IClientLicense[] {
        return (licenses ?? []).map((license) => ({
            licenseCode: license.licenseCode ?? (license as { LicenseCode?: IClientLicense['licenseCode'] }).LicenseCode!,
            isActive: license.isActive ?? (license as { IsActive?: boolean }).IsActive ?? false
        }));
    }

    private scheduleUtcMidnightRefresh(): void {
        if (this.midnightTimerId != null) {
            clearTimeout(this.midnightTimerId);
        }

        const now = new Date();
        const nextMidnight = Date.UTC(
            now.getUTCFullYear(),
            now.getUTCMonth(),
            now.getUTCDate() + 1
        );
        const delayMs = Math.max(1_000, nextMidnight - now.getTime());

        this.midnightTimerId = setTimeout(() => {
            this.commonService.applyEffectiveSubscriptionDates();
            if (this.commonService.isLocked() && this.authService.isAuthenticated()) {
                this.router.navigate(['/subscription-locked']);
            }
            this.clientContextRefresh.refreshSubscription();
            this.scheduleUtcMidnightRefresh();
        }, delayMs);
    }
}
